import { OrganizationTypes } from '@/common/constants';
import { LogoInfo } from '@/common/types';
import { PaymentMethodType } from '@/modules/employee/employee.types';
import { EmployerStatus } from '@/modules/employer/employer.constants';
import { IFileTypeDtoFileTypeEnum } from '@/types';

import { AllowAssumedContribution } from '../components/SetupEmployer/FinancialDetails/BankInformation/AccountUsage/AccountUsage.types';

import { ClaimOffsetLogic, RecurringCardSwipes } from './CreateEmployer.dto.payload';

export interface AddressesDto {
  id?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  preferred?: boolean;
  countryId?: string | number;
  addressType?: string;
}

export enum PersonThemeNamesEnum {
  CLARITY = 'CLARITY',
  ELEVATE = 'ELEVATE',
}

export interface CardProductionDate {
  cardProductionDate?: string;
  cardProductionDateState?: string;
}

export interface Bin {
  bin?: string;
  binState?: string;
}

export interface AllowedPaymentMethodConfigPayload {
  payment_employee_fee?: number | null;
  payment_method_type?: PaymentMethodType;
}
export interface AllowedPaymentMethodConfig {
  paymentEmployeeFee?: string;
  paymentMethodType?: PaymentMethodType;
}

export enum WaitingForFundsContinuePayment {
  EMPLOYEE_CHOICE = 'EMPLOYEE_CHOICE',
  AUTOPAY = 'AUTO_PAY',
}

export enum AllowRenewalsTypes {
  NO_RENEWALS = 'NO_RENEWALS',
  BY_REQUEST = 'BY_REQUEST',
  FULL_SETUP = 'FULL_SETUP',
}

export interface OverridablePropertiesDto {
  recurringCardSwipes?: RecurringCardSwipes;
  pushProvisioning?: string;
  customerServiceNumber?: string;
  ssoOnly?: boolean;
  adminSsoOnly?: boolean;
  allowEmployeesEditAddress?: boolean;
  url?: string;
  accountManagerId?: string;
  themeId?: PersonThemeNamesEnum;
  hsaCustodianId?: string;
  depositoryBankConfigType?: string | null;
  electronicFundingModeId?: string | null;
  checkFundingModel?: string | null;
  cardOffered?: boolean;
  allowDependentCard?: boolean;
  allowAdminContribution?: boolean;
  allowAssumedContribution?: AllowAssumedContribution;
  externalReplenishmentAccountMethod?: string;
  externalPayrollContributionsAccountMethod?: string;
  externalAdministratorFeesAccountMethod?: string;
  payrollContributionsHoldPeriod?: number | string;
  contributionPushGiveUpDays?: number | string;
  trueSsn?: boolean;
  idSourceId?: string;
  communicationChannels?: string[];
  communicationCategory?: string[];
  emailTemplate?: string;
  isReimbursementAllowed?: boolean;
  allowedPaymentMethods?: PaymentMethodType[];
  allowedPaymentMethodConfigs?: AllowedPaymentMethodConfig[];
  isBillPayAllowed?: boolean;
  isClaimOffsetsAllowed?: boolean;
  claimOffsetLogic?: ClaimOffsetLogic;
  waitingForFundsContinuePayment?: WaitingForFundsContinuePayment;
  waitingForFundsExpiration?: string;
  // cardProductionDate?: CardProductionDate,
  // bin?: Bin,
  prin?: string | boolean;
  isExternalLink?: boolean | string;
  replenishmentFrequencyType?: string;
  replenishmentDayNumber?: number;
  sessionTimeoutWarn?: number;
  sessionTimeoutLogout?: number;
  isFileManagerAccessEnabled?: boolean;
  isFileAutoPostEnabled?: boolean | string;
  allowedFileTypes?: IFileTypeDtoFileTypeEnum[];
  duplicateCheckFileTypes?: IFileTypeDtoFileTypeEnum[];
  logoInfo?: LogoInfo;
  replenishmentMinValue?: number;
  replenishmentPercent?: number;
  replenishmentCreepMin?: number;
  replenishmentCreepPercent?: number;
  eeContributionsHoldPeriod?: string;
  minimumPaymentReimbursementAmount?: string;
  statementType?: string;
  statementFrequencyType?: string;
  aiAdjudication?: boolean;
  isSigisAutoApproveEnabled?: boolean;
  aiConfidenceLevel?: number;
  prefundInitialDelayDate?: string | null;
  cardProcessingStartDate?: string | null;
  requireParticipantMfa?: boolean;
  requireAdminMfa?: boolean;
  accountLockoutTime?: number;
  orphanEmployer?: string;
  allowPrefund?: boolean;
  allowRenewals?: AllowRenewalsTypes;

  fileLocation?: string;
  isSameErrorReportLocation?: boolean;
  errorReportLocation?: string;
  participantPortalLink?: string | null;
  adminPortalLink?: string | null;
  zendeskSsoEnabled?: boolean;

  customReportingField1Name?: string;
  customReportingField2Name?: string;
  customReportingField3Name?: string;
  customReportingField4Name?: string;
  customReportingField5Name?: string;

  isCustomReportingField1Enabled?: boolean;
  isCustomReportingField2Enabled?: boolean;
  isCustomReportingField3Enabled?: boolean;
  isCustomReportingField4Enabled?: boolean;
  isCustomReportingField5Enabled?: boolean;

  paperStatementFeesAmount?: string;
  paperStatementFeesPaidBy?: string;

  adminChatbotLink?: string;
  automatedOrgLockEnabled?: boolean;
  automatedOrgLockEnabledStateId?: string;

  externalBalanceAdjustmentAccountMethod?: string;
  balanceAdjustmentHoldPeriod?: number | string;
  balanceAdjustmentPushGiveUpDays?: number | string;

  isFraudSingleClaimThresholdEnabled?: boolean;
  fraudSingleClaimThresholdAmount?: string;
  isFraudDailyThresholdEnabled?: boolean;
  fraudDailyThresholdAmount?: string;
  isFraudWeeklyThresholdEnabled?: boolean;
  fraudWeeklyThresholdAmount?: string;
}

export interface NormalPropertiesDto {
  id?: string;
  organizationId?: number;
  taxId?: string;
  incomingClearingCccount?: number;
  notionalClearingAccount?: number;
  hsaClearingAccount?: number;
  administratorFeesClearingAccount?: number;
  notionalAccount?: number;
  hsaAccount?: number;
  replenishmentAccount?: number;
  cardProductionDate?: string;
  externalReplenishmentAccount?: string;
  externalPayrollContributionsAccount?: string;
  externalAdministratorFeesAccount?: string;
  accountManagerId?: string;
  implementationManagerId?: string;
  externalAccountEnable?: boolean;
  externalPushReturnAccount?: string;
  contributionClearingAccount?: string;
  externalBalanceAdjustmentAccount?: string;
}

export interface OrganizationPhoneDto {
  number?: string;
  phoneExtension?: string;
}

export interface EmployerDto {
  id?: string;
  externalIdentifier?: string;
  name?: string;
  type?: OrganizationTypes;
  parentId?: string | number;
  grandparentId?: string | number;
  addresses?: AddressesDto[];
  overridableProperties?: OverridablePropertiesDto;
  normalProperties?: NormalPropertiesDto;
  notes?: string;
  shortName?: string;
  businessEntityType?: string | number;
  organizationPhone?: OrganizationPhoneDto;
  organizationStatusType?: EmployerStatus;
  isActivationStarted?: boolean;
  isTerminated?: boolean;
  createdAt?: string;
  themeId?: PersonThemeNamesEnum;
  isLimitedFeaturesDuringTerminationInProgress?: boolean;
}
